import React, { useState } from 'react';
import packageJson from '../../package.json';
import styled, { keyframes } from 'styled-components';
import { ChevronRight, User, Key, Lock, Info, FileText, ScrollText, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useKoffieNav } from './KoffieApp';

// ─── Tokens ───────────────────────────────────────────────────────────────────

const t = {
  bg:     '#f2f0eb',
  white:  '#ffffff',
  text:   '#0c0c0b',
  muted:  '#8a8780',
  body:   '#5b5953',
  lines:  '#dcd9d1',
  accent: '#c75a0a',
  sans:   "'Jost', system-ui, sans-serif",
  serif:  "'Playfair Display', Georgia, serif",
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  height: 100%;
  width: 100%;
  background: ${t.bg};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Page = styled.div`
  padding: 36px 40px 80px;
  max-width: 560px;
  margin: 0 auto;
  @media (max-width: 600px) {
    padding: 24px 20px 64px;
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const PageTitle = styled.h1`
  font-family: ${t.serif};
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 900;
  color: ${t.text};
  letter-spacing: -0.02em;
  margin: 0 0 28px;
  line-height: 1.1;
`;

// ─── Section ──────────────────────────────────────────────────────────────────

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionLabel = styled.div`
  font-family: ${t.sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${t.muted};
  margin-bottom: 8px;
  padding-left: 2px;
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  overflow: hidden;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  padding: 13px 16px;
  background: none;
  border: none;
  border-bottom: 1px solid ${t.lines};
  cursor: ${p => p.$static ? 'default' : 'pointer'};
  width: 100%;
  text-align: left;
  font-family: ${t.sans};
  transition: background 0.12s;

  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.$static ? 'transparent' : t.bg}; }
`;

const IconBox = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$bg || t.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  color: ${p => p.$color || t.muted};
`;

const RowLabel = styled.div`
  flex: 1;
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$destructive ? '#ef4444' : t.text};
`;

const RowValue = styled.div`
  font-family: ${t.sans};
  font-size: 12px;
  color: ${t.muted};
  margin-right: 6px;
`;

const Chevron = styled.div`
  color: ${t.muted};
  opacity: 0.35;
`;

// ─── Account row ──────────────────────────────────────────────────────────────

const AccountAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${t.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  color: #fff;
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
`;

const AccountName = styled.div`
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 600;
  color: ${t.text};
`;

const AccountRole = styled.div`
  font-family: ${t.sans};
  font-size: 11px;
  color: ${t.muted};
  margin-top: 2px;
`;

// ─── Logout modal ─────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12,12,11,0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: ${t.white};
  border: 1px solid ${t.lines};
  border-radius: 8px;
  max-width: 360px;
  width: 90%;
  padding: 24px 22px 20px;
`;

const ModalTitle = styled.h2`
  font-family: ${t.serif};
  font-size: 20px;
  font-weight: 900;
  color: ${t.text};
  margin: 0 0 8px;
  letter-spacing: -0.02em;
`;

const ModalText = styled.p`
  font-family: ${t.sans};
  font-size: 13px;
  color: ${t.muted};
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ModalButtons = styled.div`display: flex; gap: 8px;`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 6px;
  font-family: ${t.sans};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
  border: 1px solid ${p => p.$danger ? 'transparent' : t.lines};
  background: ${p => p.$danger ? '#ef4444' : t.white};
  color: ${p => p.$danger ? '#fff' : t.text};
  &:hover { background: ${p => p.$danger ? '#dc2626' : t.bg}; }
`;

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 9px 18px;
  border-radius: 6px;
  font-family: ${t.sans};
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.2s ease;
  background: ${t.text};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function KoffieSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const { handleNavigation } = useKoffieNav();
  const [showLogout, setShowLogout] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const username = user?.username || '—';
  const roleLabel = { 1: 'Ontwikkelaar', 2: 'Eigenaar', 3: 'Medewerker' }[user?.role_id] || 'Gebruiker';
  const initials = username.slice(0, 2).toUpperCase();

  return (
    <Wrap>
      <Page>
        <PageTitle>Instellingen</PageTitle>

        {/* Account */}
        <Section>
          <SectionLabel>Account</SectionLabel>
          <Card>
            <Row $static>
              <AccountAvatar>{initials}</AccountAvatar>
              <div>
                <AccountName>{username}</AccountName>
                <AccountRole>Koffiereparatie Geleen — {roleLabel}</AccountRole>
              </div>
            </Row>
          </Card>
        </Section>

        {/* Beveiliging */}
        <Section>
          <SectionLabel>Beveiliging</SectionLabel>
          <Card>
            <Row onClick={() => handleNavigation(() => navigate('/Koffiereparatie/settings/password'))}>
              <IconBox $color="#3b82f6" $bg="rgba(59,130,246,0.08)"><Key size={14} /></IconBox>
              <RowLabel>Wachtwoord wijzigen</RowLabel>
              <Chevron><ChevronRight size={14} /></Chevron>
            </Row>
            <Row onClick={() => handleNavigation(() => navigate('/Koffiereparatie/settings/pin'))}>
              <IconBox $color="#22c55e" $bg="rgba(34,197,94,0.08)"><Lock size={14} /></IconBox>
              <RowLabel>Pincode</RowLabel>
              <Chevron><ChevronRight size={14} /></Chevron>
            </Row>
          </Card>
        </Section>

        {/* Website inhoud */}
        <Section>
          <SectionLabel>Website inhoud</SectionLabel>
          <Card>
            <Row onClick={() => handleNavigation(() => navigate('/Koffiereparatie/settings/terms'))}>
              <IconBox><ScrollText size={14} /></IconBox>
              <RowLabel>Algemene voorwaarden</RowLabel>
              <Chevron><ChevronRight size={14} /></Chevron>
            </Row>
          </Card>
        </Section>

        {/* Over */}
        <Section>
          <SectionLabel>Over</SectionLabel>
          <Card>
            <Row $static>
              <IconBox><Info size={14} /></IconBox>
              <RowLabel>Versie</RowLabel>
              <RowValue>v{packageJson.version}</RowValue>
            </Row>
            <Row onClick={() => handleNavigation(() => navigate('/Koffiereparatie/settings/legal'))}>
              <IconBox><FileText size={14} /></IconBox>
              <RowLabel>Wettelijke documentatie</RowLabel>
              <Chevron><ChevronRight size={14} /></Chevron>
            </Row>
          </Card>
        </Section>

        {/* Uitloggen */}
        <Section>
          <Card>
            <Row onClick={() => setShowLogout(true)}>
              <IconBox $color="#ef4444" $bg="rgba(239,68,68,0.08)"><LogOut size={14} /></IconBox>
              <RowLabel $destructive>Uitloggen</RowLabel>
            </Row>
          </Card>
        </Section>
      </Page>

      {toast && <Toast>{toast}</Toast>}

      {showLogout && (
        <Overlay onClick={() => setShowLogout(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Uitloggen</ModalTitle>
            <ModalText>Weet je zeker dat je wilt uitloggen?</ModalText>
            <ModalButtons>
              <ModalBtn onClick={() => setShowLogout(false)}>Annuleren</ModalBtn>
              <ModalBtn $danger onClick={() => { setShowLogout(false); onLogout(); }}>Uitloggen</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
